import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Users, Calendar, Building2, Paperclip, AlertCircle, CheckCircle, XCircle, Linkedin, Download } from 'lucide-react';
import { apiService, contactsAPI } from '../services/api';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import './EventAttendees.css'; // Reuse attendee drawer styles
import '../pages/EventEntityCompanies.css'; // For entity profile drawer styles
import './MeetingDetailsDrawer.css'; // Must be last for z-index overrides

const MeetingDetailsDrawer = ({ meetingId, isOpen, onClose, onMeetingCancelled, activeTab, counterpartyCompanyName, counterpartyEntityType, counterpartyEventCompanyId }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [meetingData, setMeetingData] = useState(null);
    const [eventData, setEventData] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [cancelError, setCancelError] = useState(null);

    // Cancel flow state
    const [showCancelPanel, setShowCancelPanel] = useState(false);
    const [cancelMessage, setCancelMessage] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Accept/Decline state
    const [accepting, setAccepting] = useState(false);
    const [declining, setDeclining] = useState(false);
    const [actionError, setActionError] = useState(null);

    // Decline flow state
    const [showDeclinePanel, setShowDeclinePanel] = useState(false);
    const [declineMessage, setDeclineMessage] = useState('');

    // Accept flow state
    const [showAcceptPanel, setShowAcceptPanel] = useState(false);
    const [acceptMessage, setAcceptMessage] = useState('');

    // Post-optimization accept flow state
    const [availabilitySlots, setAvailabilitySlots] = useState([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [loadingAvailability, setLoadingAvailability] = useState(false);
    const [availabilityError, setAvailabilityError] = useState(null);

    // Reschedule flow state
    const [showReschedulePanel, setShowReschedulePanel] = useState(false);
    const [rescheduleMessage, setRescheduleMessage] = useState('');
    const [rescheduling, setRescheduling] = useState(false);
    const [rescheduleSlots, setRescheduleSlots] = useState([]);
    const [selectedRescheduleSlot, setSelectedRescheduleSlot] = useState(null);
    const [loadingRescheduleAvailability, setLoadingRescheduleAvailability] = useState(false);
    const [rescheduleError, setRescheduleError] = useState(null);

    // Participant profile drawer state
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
    const [participantProfileData, setParticipantProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);

    // Entity profile drawer state (for nonprofit/philanthropist companies)
    const [entityProfileDrawerOpen, setEntityProfileDrawerOpen] = useState(false);
    const [entityProfileData, setEntityProfileData] = useState(null);
    const [entityProfileLoading, setEntityProfileLoading] = useState(false);
    const [entityQuestions, setEntityQuestions] = useState([]);

    const cancelPanelRef = useRef(null);
    const declinePanelRef = useRef(null);
    const acceptPanelRef = useRef(null);
    const reschedulePanelRef = useRef(null);
    const drawerContentRef = useRef(null);

    useEffect(() => {
        if (isOpen && meetingId) {
            fetchMeetingDetails();
            // Reset cancel flow state when drawer opens
            setShowCancelPanel(false);
            setCancelMessage('');
            setShowConfirmDialog(false);
            setCancelError(null);
            // Reset accept/decline state
            setActionError(null);
            // Reset decline flow state
            setShowDeclinePanel(false);
            setDeclineMessage('');
            // Reset accept flow state
            setShowAcceptPanel(false);
            setAcceptMessage('');
            // Reset availability state
            setAvailabilitySlots([]);
            setSelectedTimeSlot(null);
            setLoadingAvailability(false);
            setAvailabilityError(null);
            // Reset reschedule flow state
            setShowReschedulePanel(false);
            setRescheduleMessage('');
            setRescheduling(false);
            setRescheduleSlots([]);
            setSelectedRescheduleSlot(null);
            setLoadingRescheduleAvailability(false);
            setRescheduleError(null);
            // Reset participant profile drawer state
            setSelectedParticipant(null);
            setProfileDrawerOpen(false);
            setParticipantProfileData(null);
            setProfileLoading(false);
            // Reset entity profile drawer state
            setEntityProfileDrawerOpen(false);
            setEntityProfileData(null);
            setEntityProfileLoading(false);
            setEntityQuestions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, meetingId]);

    // Scroll to cancel panel when it becomes visible
    useEffect(() => {
        if (showCancelPanel && cancelPanelRef.current) {
            cancelPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [showCancelPanel]);

    // Scroll to decline panel when it becomes visible
    useEffect(() => {
        if (showDeclinePanel && declinePanelRef.current) {
            declinePanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [showDeclinePanel]);

    // Scroll to accept panel when it becomes visible
    useEffect(() => {
        if (showAcceptPanel && acceptPanelRef.current) {
            acceptPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [showAcceptPanel]);

    // Scroll to reschedule panel when it becomes visible
    useEffect(() => {
        if (showReschedulePanel && reschedulePanelRef.current) {
            reschedulePanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [showReschedulePanel]);

    const fetchMeetingDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiService.getMeetingDetails(meetingId);
            if (response.data.success) {
                setMeetingData(response.data.meeting);
                setEventData(response.data.event);
            } else {
                setError(response.data.error || 'Failed to load meeting details');
            }
        } catch (err) {
            console.error('Error fetching meeting details:', err);
            setError('Failed to load meeting details');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelInviteClick = () => {
        setShowCancelPanel(true);
    };

    const handleCancelPanelClose = () => {
        setShowCancelPanel(false);
        setCancelMessage('');
        setCancelError(null);
        // Scroll back to top
        if (drawerContentRef.current) {
            drawerContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleSendCancelClick = () => {
        setShowConfirmDialog(true);
    };

    const handleConfirmDialogClose = () => {
        setShowConfirmDialog(false);
    };

    const handleConfirmCancel = async () => {
        setCancelling(true);
        setCancelError(null);
        try {
            const response = await apiService.cancelMeeting(meetingId, cancelMessage);
            if (response.data.success) {
                setShowConfirmDialog(false);
                setShowCancelPanel(false);
                if (onMeetingCancelled) {
                    onMeetingCancelled(meetingId);
                }
                onClose();
            } else {
                setCancelError(response.data.error || 'Failed to cancel meeting');
                setShowConfirmDialog(false);
            }
        } catch (err) {
            console.error('Error cancelling meeting:', err);
            setCancelError('Failed to cancel meeting');
            setShowConfirmDialog(false);
        } finally {
            setCancelling(false);
        }
    };

    const handleAttachmentClick = async (e, documentUrl) => {
        e.preventDefault();
        try {
            const response = await apiService.getFileDownloadUrl(documentUrl);
            if (response.data.success && response.data.url) {
                window.open(response.data.url, '_blank');
            } else {
                console.error('Failed to get download URL:', response.data.error);
                alert('Failed to open attachment. Please try again.');
            }
        } catch (err) {
            console.error('Error getting download URL:', err);
            alert('Failed to open attachment. Please try again.');
        }
    };

    // Get the event timezone - use event's timezone for in-person events so all attendees see consistent times
    const getEventTimezone = () => {
        return eventData?.timeZone || eventData?.TimeZone || 'America/New_York';
    };

    const formatDateTime = (dateString, includeTimezone = true) => {
        if (!dateString) return 'TBD';
        const date = new Date(dateString);
        const timezone = getEventTimezone();
        const options = {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: timezone
        };
        if (includeTimezone) {
            options.timeZoneName = 'short';
        }
        return date.toLocaleString('en-US', options);
    };

    const getTimezoneAbbr = () => {
        const timezone = getEventTimezone();
        // Format a date in the event timezone to get the timezone abbreviation
        const date = new Date();
        const parts = date.toLocaleString('en-US', { timeZone: timezone, timeZoneName: 'short' }).split(' ');
        return parts[parts.length - 1]; // Get the last part which is the timezone
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return '';

        // Handle date strings with too many decimal places (e.g., "2026-01-08T17:24:17.8200000")
        // Normalize to 3 decimal places max for proper JS Date parsing
        let normalizedDateString = dateString;
        const decimalMatch = dateString.match(/\.(\d+)/);
        if (decimalMatch && decimalMatch[1].length > 3) {
            normalizedDateString = dateString.replace(/\.(\d{3})\d*/, '.$1');
        }

        // Ensure the string is treated as UTC by appending 'Z' if not present
        if (!normalizedDateString.endsWith('Z') && !normalizedDateString.includes('+') && !normalizedDateString.includes('-', 10)) {
            normalizedDateString += 'Z';
        }

        const now = new Date();
        const date = new Date(normalizedDateString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            console.error('Invalid date:', dateString);
            return '';
        }

        const seconds = Math.floor((now - date) / 1000);

        // Handle future dates or clock skew
        if (seconds < 0) {
            return 'just now';
        }

        if (seconds < 60) {
            return seconds <= 1 ? 'just now' : `${seconds} seconds ago`;
        }

        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) {
            return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
        }

        const hours = Math.floor(minutes / 60);
        if (hours < 24) {
            return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
        }

        const days = Math.floor(hours / 24);
        if (days < 7) {
            return days === 1 ? '1 day ago' : `${days} days ago`;
        }

        const weeks = Math.floor(days / 7);
        if (weeks < 4) {
            return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
        }

        const months = Math.floor(days / 30);
        if (months < 12) {
            return months === 1 ? '1 month ago' : `${months} months ago`;
        }

        const years = Math.floor(days / 365);
        return years === 1 ? '1 year ago' : `${years} years ago`;
    };

    const getStatusLabel = (statusId) => {
        const statuses = {
            1: 'Pending',
            2: 'Confirmed',
            3: 'Cancelled',
            4: 'Declined',
            5: 'Expired'
        };
        return statuses[statusId] || 'Unknown';
    };

    const getStatusClass = (statusId) => {
        const classes = {
            1: 'status-pending',
            2: 'status-confirmed',
            3: 'status-cancelled',
            4: 'status-declined',
            5: 'status-expired'
        };
        return classes[statusId] || 'status-default';
    };

    // Get the other company name (the counterparty's company)
    // Uses the company name passed from the list, or falls back to 'Meeting'
    const getOtherCompanyName = () => {
        return counterpartyCompanyName || 'Meeting';
    };

    // Get participant's company name (handles both flat and nested structures)
    const getParticipantCompanyName = (participant) => {
        return participant.companyName || participant.company?.name || '';
    };

    // Check if participant belongs to counterparty company
    const isCounterpartyParticipant = (participant) => {
        const participantCompany = getParticipantCompanyName(participant);
        // Compare company names (case-insensitive, trimmed)
        return participantCompany.toLowerCase().trim() === (counterpartyCompanyName || '').toLowerCase().trim();
    };

    // Check if counterparty is a nonprofit or philanthropist
    // eslint-disable-next-line no-unused-vars
    const isEntityProfileType = () => {
        // Try prop first, then meeting data
        const entityType = (counterpartyEntityType || meetingData?.counterpartyEntityType)?.toLowerCase();
        return entityType === 'nonprofit' || entityType === 'philanthropist';
    };

    // Get the counterparty's ic_event_company_id from meeting data
    const getCounterpartyEventCompanyId = () => {
        // If passed as prop, use it
        if (counterpartyEventCompanyId) return counterpartyEventCompanyId;
        
        // Otherwise, determine from meetingData based on who is the organizer
        if (!meetingData) return null;
        
        // If current user is the organizer, counterparty is the participant
        // If current user is the participant (receiver), counterparty is the organizer
        if (isCurrentUserOrganizer()) {
            return meetingData.participantEventCompanyId;
        } else {
            return meetingData.organizerEventCompanyId;
        }
    };

    // Handle participant click to open profile drawer
    const handleParticipantClick = async (participant) => {
        if (!participant.contactId) return;

        setSelectedParticipant(participant);

        const eventCompanyId = getCounterpartyEventCompanyId();
        const isCounterparty = isCounterpartyParticipant(participant);

        console.log('[MeetingDetailsDrawer] Participant click:', {
            participantCompany: getParticipantCompanyName(participant),
            counterpartyCompanyName,
            isCounterparty,
            eventCompanyId
        });

        // If this is a counterparty participant and we have their event company ID,
        // try to fetch their entity profile from the database
        if (isCounterparty && eventCompanyId) {
            try {
                // Fetch the entity company profile - the backend will determine the entity type
                const response = await api.get(`/event-entity-companies/by-event-company/${eventCompanyId}/profile`);
                
                console.log('[MeetingDetailsDrawer] Entity profile response:', response.data);
                
                if (response.data) {
                    // Normalize entity name for comparison (handle "Non-Profit", "Nonprofit", "non-profit", etc.)
                    const entityName = (response.data.entity_name || '').toLowerCase().replace(/[^a-z]/g, '');
                    
                    console.log('[MeetingDetailsDrawer] Normalized entity name:', entityName);
                    
                    // Check if this is a nonprofit or philanthropist based on the entity_name from DB
                    if (entityName.includes('nonprofit') || entityName.includes('philanthropist')) {
                        console.log('[MeetingDetailsDrawer] Opening entity profile drawer for:', entityName);
                        // Open entity profile drawer
                        setEntityProfileDrawerOpen(true);
                        setEntityProfileLoading(false);
                        setEntityProfileData(response.data.company);
                        setEntityQuestions(response.data.questions || []);
                        return; // Exit early - we're showing the entity drawer
                    }
                }
            } catch (err) {
                console.error('Error fetching entity profile:', err);
                // Fall through to show contact profile
            }
        }

        // Default: Open regular contact profile drawer
        // Note: participant.contactId is ic_contact_id from iConnections, not F4C contact ID
        // This may return 404 if we don't have the contact in F4C database
        setProfileDrawerOpen(true);
        setProfileLoading(true);

        try {
            const response = await contactsAPI.getPublicProfile(participant.contactId);
            setParticipantProfileData(response.data);
        } catch (err) {
            console.error('Error fetching participant profile:', err);
            setParticipantProfileData(null);
        } finally {
            setProfileLoading(false);
        }
    };

    // Close participant profile drawer
    const closeProfileDrawer = () => {
        setProfileDrawerOpen(false);
        setSelectedParticipant(null);
        setParticipantProfileData(null);
    };

    // Close entity profile drawer
    const closeEntityProfileDrawer = () => {
        setEntityProfileDrawerOpen(false);
        setEntityProfileData(null);
        setEntityQuestions([]);
    };

    // Render drawer value for entity profile (similar to EventNonprofits)
    const renderEntityDrawerValue = (value, question) => {
        if (!value) {
            return <span className="drawer-empty-value">Not provided</span>;
        }

        if (typeof value === 'object' && value !== null && value.type === 'file' && value.url) {
            const isImage = value.url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
            const s3Key = value.s3_key || value.url;

            const handleFileClick = async (e) => {
                e.preventDefault();
                try {
                    const companyName = entityProfileData?.CompanyName || '';
                    const questionLabel = question.short_description || question.question_text || '';
                    const response = await api.post('/event-entity-companies/file-download', null, {
                        params: {
                            s3_key: s3Key,
                            company_name: companyName,
                            question_label: questionLabel
                        }
                    });
                    if (response.data?.presigned_url) {
                        window.open(response.data.presigned_url, '_blank');
                    }
                } catch (err) {
                    console.error('Error fetching file URL:', err);
                    window.open(value.url, '_blank');
                }
            };

            return (
                <div className="drawer-file-section">
                    {isImage ? (
                        <div className="drawer-image-preview">
                            <img src={value.url} alt={question.question_text} />
                        </div>
                    ) : null}
                    <button onClick={handleFileClick} className="drawer-file-link">
                        <Download size={16} />
                        View File
                    </button>
                </div>
            );
        }

        // Check field type for chip rendering
        const fieldName = (question.short_description || question.question_text || '').toLowerCase();
        const isAreasOfInterest = fieldName.includes('areas of interest') || fieldName.includes('focus area') || fieldName.includes('areas of philanthropic giving');
        const isGeographicFocus = (fieldName.includes('geographic') && (fieldName.includes('footprint') || fieldName.includes('focus'))) || fieldName.includes('potential geographies');

        // Check if this is hierarchical data
        const isHierarchicalTags = typeof value === 'object' && value !== null && value.type === 'hierarchical' && Array.isArray(value.items);

        // Handle hierarchical values as chips
        if (isHierarchicalTags) {
            return (
                <div className="nonprofit-card-tags">
                    {value.items.map((item, idx) => (
                        <span key={idx} className="nonprofit-tag">
                            {item.category}
                            {item.subcategories && item.subcategories.length > 0 && (
                                <span className="nonprofit-tag-subs">
                                    {item.subcategories.map((sub, subIdx) => (
                                        <span key={subIdx}> [{sub}]{subIdx < item.subcategories.length - 1 ? ',' : ''}</span>
                                    ))}
                                </span>
                            )}
                        </span>
                    ))}
                </div>
            );
        }

        // Render Areas of Interest or Geographic Focus as chips when value is comma-separated string
        if ((isAreasOfInterest || isGeographicFocus) && typeof value === 'string') {
            const tags = value.split(',').map(t => t.trim()).filter(t => t);
            if (tags.length > 0) {
                return (
                    <div className="nonprofit-card-tags">
                        {tags.map((tag, idx) => (
                            <span key={idx} className="nonprofit-tag">{tag}</span>
                        ))}
                    </div>
                );
            }
        }

        const textValue = String(value);

        // Check if it's a URL
        const isUrl = textValue.match(/^(https?:\/\/|www\.)/i) ||
            (textValue.includes('.') && !textValue.includes(' ') &&
                (question.question_text?.toLowerCase().includes('website') ||
                    question.short_description?.toLowerCase().includes('website')));

        if (isUrl) {
            const href = textValue.startsWith('http') ? textValue : `https://${textValue}`;
            return (
                <div className="drawer-text-value">
                    <a href={href} target="_blank" rel="noopener noreferrer" className="drawer-link">
                        {textValue}
                    </a>
                </div>
            );
        }

        return <div className="drawer-text-value">{textValue}</div>;
    };

    // Get initials for profile avatar
    const getProfileInitials = () => {
        const first = selectedParticipant?.firstName?.[0] || '';
        const last = selectedParticipant?.lastName?.[0] || '';
        return (first + last).toUpperCase();
    };

    // Get current user's ic_contact_guid from localStorage
    const getCurrentUserContactGuid = () => {
        try {
            const userData = localStorage.getItem('f4c_web_user');
            if (userData) {
                const parsed = JSON.parse(userData);
                return parsed.ic_contact_guid;
            }
        } catch (e) {
            console.error('Error parsing user data:', e);
        }
        return null;
    };

    // Check if current user is the organizer (compare by contactGuid)
    const isCurrentUserOrganizer = () => {
        const currentUserGuid = getCurrentUserContactGuid();
        if (!currentUserGuid) return false;

        const organizer = meetingData?.participants?.find(p => p.isOriginalMeetingOrganizer);
        if (!organizer?.contactGuid) return false;

        // Compare GUIDs (case-insensitive)
        return currentUserGuid.toUpperCase() === organizer.contactGuid.toUpperCase();
    };

    // Check if current user can cancel a pending invite (organizer of pending meeting)
    const canCancelPendingInvite = () => {
        if (meetingData?.meetingStatusId !== 1) return false; // Only pending
        return isCurrentUserOrganizer();
    };

    // Check if current user can cancel a confirmed meeting (any party can cancel)
    const canCancelConfirmed = () => {
        return meetingData?.meetingStatusId === 2; // Any confirmed meeting
    };

    // Check if current user can accept/decline (receiver of pending meeting)
    const canAcceptDecline = () => {
        if (meetingData?.meetingStatusId !== 1) return false; // Only pending
        return !isCurrentUserOrganizer(); // Only if NOT the organizer
    };

    // Check if this is pre-optimization (hasOptimization = true, isOptimized = false)
    // eslint-disable-next-line no-unused-vars
    const isPreOptimization = () => {
        return eventData?.hasOptimization === true && eventData?.isOptimized === false;
    };

    // Check if this is post-optimization (requires time slot selection)
    // Post-optimization when: (hasOptimization=true AND isOptimized=true) OR hasOptimization=false
    const isPostOptimization = () => {
        // // TODO: Remove this line after testing
        //return true; // TEMPORARY: Force post-optimization for testing
        return (eventData?.hasOptimization === true && eventData?.isOptimized === true) ||
            eventData?.hasOptimization === false;
    };

    // Fetch mutual availability for post-optimization acceptance
    const fetchMutualAvailability = async () => {
        if (!meetingData?.organizerEventCompanyId || !meetingData?.participantEventCompanyId) {
            setAvailabilityError('Missing company information for availability check');
            return;
        }

        setLoadingAvailability(true);
        setAvailabilityError(null);
        setAvailabilitySlots([]);
        setSelectedTimeSlot(null);

        try {
            const response = await apiService.getMutualAvailability(
                meetingData.organizerEventCompanyId,
                meetingData.participantEventCompanyId
            );

            if (response.data.success) {
                const allSlots = response.data.availability || [];
                // TODO: Remove this filter after testing - only show times on 2026-02-25
                const slots = allSlots.filter(slot => {
                    const date = new Date(slot.meetingDateTimeUtc);
                    return date.getUTCFullYear() === 2026 &&
                        date.getUTCMonth() === 1 && // February is month 1 (0-indexed)
                        date.getUTCDate() === 25;
                });
                setAvailabilitySlots(slots);
                if (slots.length === 0) {
                    setAvailabilityError('No mutual availability found. You cannot accept this meeting at this time.');
                }
            } else {
                setAvailabilityError(response.data.error || 'Failed to fetch availability');
            }
        } catch (err) {
            console.error('Error fetching mutual availability:', err);
            setAvailabilityError('Failed to fetch availability');
        } finally {
            setLoadingAvailability(false);
        }
    };

    // Validate availability for pre-optimization
    const validatePreOptimizationAvailability = async () => {
        if (!meetingData || !eventData) return false;

        setLoadingAvailability(true);
        setAvailabilityError(null);

        try {
            const response = await apiService.validateAvailability(
                eventData.eventId,
                meetingData.organizerEventCompanyId,
                meetingData.participantEventCompanyId
            );

            if (response.data.success) {
                return true;
            } else {
                setAvailabilityError(response.data.error || "You don't have available times");
                return false;
            }
        } catch (err) {
            console.error('Error validating availability:', err);
            setAvailabilityError('Failed to validate availability');
            return false;
        } finally {
            setLoadingAvailability(false);
        }
    };

    // Handle accept button click
    const handleAcceptClick = async () => {
        if (isPostOptimization()) {
            // Post-optimization: fetch mutual availability and show time selection
            setShowAcceptPanel(true);
            await fetchMutualAvailability();
        } else {
            // Pre-optimization: validate availability first
            setShowAcceptPanel(true);
            const isValid = await validatePreOptimizationAvailability();
            if (!isValid) {
                // Keep panel open to show error message
                return;
            }
        }
    };

    // Handle accept panel close
    const handleAcceptPanelClose = () => {
        setShowAcceptPanel(false);
        setAcceptMessage('');
        setActionError(null);
        setAvailabilitySlots([]);
        setSelectedTimeSlot(null);
        setAvailabilityError(null);
        // Scroll back to top
        if (drawerContentRef.current) {
            drawerContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Format availability slot for display - uses event timezone
    const formatAvailabilitySlot = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const timezone = getEventTimezone();
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: timezone
        });
    };

    // Handle send accept with message
    const handleSendAccept = async () => {
        // For post-optimization, require a selected time slot
        if (isPostOptimization() && !selectedTimeSlot) {
            setActionError('Please select a meeting time');
            return;
        }

        setAccepting(true);
        setActionError(null);
        try {
            const meetingTimeId = isPostOptimization() ? selectedTimeSlot : null;
            const response = await apiService.acceptMeeting(meetingId, acceptMessage, meetingTimeId);
            if (response.data.success) {
                setShowAcceptPanel(false);
                if (onMeetingCancelled) {
                    onMeetingCancelled(meetingId); // Refresh the list
                }
                // Refresh the pending meetings count in the nav
                window.dispatchEvent(new Event('refreshPendingMeetingsCount'));
                onClose();
            } else {
                setActionError(response.data.error || 'Failed to accept meeting');
            }
        } catch (err) {
            console.error('Error accepting meeting:', err);
            setActionError('Failed to accept meeting');
        } finally {
            setAccepting(false);
        }
    };

    // Handle decline button click - show decline panel
    const handleDeclineClick = () => {
        setShowDeclinePanel(true);
    };

    // Handle decline panel close
    const handleDeclinePanelClose = () => {
        setShowDeclinePanel(false);
        setDeclineMessage('');
        setActionError(null);
        // Scroll back to top
        if (drawerContentRef.current) {
            drawerContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Handle send decline with message
    const handleSendDecline = async () => {
        setDeclining(true);
        setActionError(null);
        try {
            const response = await apiService.declineMeeting(meetingId, declineMessage);
            if (response.data.success) {
                setShowDeclinePanel(false);
                if (onMeetingCancelled) {
                    onMeetingCancelled(meetingId); // Refresh the list
                }
                // Refresh the pending meetings count in the nav
                window.dispatchEvent(new Event('refreshPendingMeetingsCount'));
                onClose();
            } else {
                setActionError(response.data.error || 'Failed to decline meeting');
            }
        } catch (err) {
            console.error('Error declining meeting:', err);
            setActionError('Failed to decline meeting');
        } finally {
            setDeclining(false);
        }
    };

    // Fetch mutual availability for rescheduling
    const fetchRescheduleAvailability = async () => {
        if (!meetingData?.organizerEventCompanyId || !meetingData?.participantEventCompanyId) {
            setRescheduleError('Missing company information for availability check');
            return;
        }

        setLoadingRescheduleAvailability(true);
        setRescheduleError(null);
        setRescheduleSlots([]);
        setSelectedRescheduleSlot(null);

        try {
            const response = await apiService.getMutualAvailability(
                meetingData.organizerEventCompanyId,
                meetingData.participantEventCompanyId
            );

            if (response.data.success) {
                const allSlots = response.data.availability || [];
                // TODO: Remove this filter after testing - only show times on 2026-02-25
                const slots = allSlots.filter(slot => {
                    const date = new Date(slot.meetingDateTimeUtc);
                    return date.getUTCFullYear() === 2026 &&
                        date.getUTCMonth() === 1 && // February is month 1 (0-indexed)
                        date.getUTCDate() === 25;
                });
                setRescheduleSlots(slots);
                if (slots.length === 0) {
                    setRescheduleError('No mutual availability found. You cannot reschedule this meeting at this time.');
                }
            } else {
                setRescheduleError(response.data.error || 'Failed to fetch availability');
            }
        } catch (err) {
            console.error('Error fetching mutual availability for reschedule:', err);
            setRescheduleError('Failed to fetch availability');
        } finally {
            setLoadingRescheduleAvailability(false);
        }
    };

    // Handle reschedule button click
    const handleRescheduleClick = async () => {
        setShowReschedulePanel(true);
        await fetchRescheduleAvailability();
    };

    // Handle reschedule panel close
    const handleReschedulePanelClose = () => {
        setShowReschedulePanel(false);
        setRescheduleMessage('');
        setRescheduleError(null);
        setRescheduleSlots([]);
        setSelectedRescheduleSlot(null);
        // Scroll back to top
        if (drawerContentRef.current) {
            drawerContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Handle send reschedule with new time
    const handleSendReschedule = async () => {
        if (!selectedRescheduleSlot) {
            setRescheduleError('Please select a new meeting time');
            return;
        }

        setRescheduling(true);
        setRescheduleError(null);
        try {
            // Use the accept endpoint with the new meetingTimeId to reschedule
            const response = await apiService.acceptMeeting(meetingId, rescheduleMessage, selectedRescheduleSlot);
            if (response.data.success) {
                setShowReschedulePanel(false);
                if (onMeetingCancelled) {
                    onMeetingCancelled(meetingId); // Refresh the list
                }
                onClose();
            } else {
                setRescheduleError(response.data.error || 'Failed to reschedule meeting');
            }
        } catch (err) {
            console.error('Error rescheduling meeting:', err);
            setRescheduleError('Failed to reschedule meeting');
        } finally {
            setRescheduling(false);
        }
    };

    // Get all participants sorted with organizer first
    const getAllParticipantsSorted = () => {
        if (!meetingData?.participants) return [];
        const organizer = meetingData.participants.find(p => p.isOriginalMeetingOrganizer);
        const others = meetingData.participants.filter(p => !p.isOriginalMeetingOrganizer);
        return organizer ? [organizer, ...others] : others;
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="drawer-overlay" onClick={onClose} />
            <div className={`meeting-drawer ${isOpen ? 'open' : ''}`}>
                <div className="drawer-header">
                    <h2 title={loading ? '' : `Meeting with ${getOtherCompanyName()}`}>
                        Meeting with {loading ? '...' : getOtherCompanyName()}
                    </h2>
                    <button className="drawer-close-btn" onClick={onClose} title="Close">
                        <X size={20} />
                    </button>
                </div>

                <div className="drawer-content" ref={drawerContentRef}>
                    {loading ? (
                        <div className="drawer-loading">
                            <LoadingSpinner message="Loading meeting details..." />
                        </div>
                    ) : error ? (
                        <div className="drawer-error">
                            <AlertCircle size={48} />
                            <h3>Error</h3>
                            <p>{error}</p>
                            <button onClick={fetchMeetingDetails} className="retry-btn">
                                Try Again
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Status Badge */}
                            <div className="drawer-section">
                                <span className={`drawer-status-badge ${getStatusClass(meetingData?.meetingStatusId)}`}>
                                    {getStatusLabel(meetingData?.meetingStatusId)}
                                </span>
                            </div>

                            {/* Where */}
                            <div className="drawer-section">
                                <div className="drawer-section-header">
                                    <MapPin size={18} />
                                    <span>Where</span>
                                </div>
                                <div className="drawer-section-content">
                                    {(() => {
                                        const loc = meetingData?.meetingLocation || eventData?.eventLocation || 'TBD';
                                        return loc === 'Sponsor Lounge' ? 'F4C - Rotating Booth' : loc;
                                    })()}
                                </div>
                            </div>

                            {/* When */}
                            {meetingData?.meetingStartDateTimeUtc && (
                                <div className="drawer-section">
                                    <div className="drawer-section-header">
                                        <Calendar size={18} />
                                        <span>When</span>
                                    </div>
                                    <div className="drawer-section-content">
                                        {formatDateTime(meetingData.meetingStartDateTimeUtc, false)}
                                        {meetingData.meetingEndDateTimeUtc && (
                                            <span> - {new Date(meetingData.meetingEndDateTimeUtc).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: getEventTimezone() })}</span>
                                        )}
                                        <span> {getTimezoneAbbr()}</span>
                                    </div>
                                </div>
                            )}

                            {/* Participants */}
                            <div className="drawer-section">
                                <div className="drawer-section-header">
                                    <Users size={16} />
                                    <span>Participants ({getAllParticipantsSorted().length})</span>
                                </div>
                                <div className="drawer-section-content">
                                    <div className="participants-list">
                                        {getAllParticipantsSorted().map((participant) => (
                                            <div
                                                key={participant.contactId}
                                                className="participant-card clickable"
                                                onClick={() => handleParticipantClick(participant)}
                                                title="View profile"
                                            >
                                                <div className="participant-avatar">
                                                    {participant.imageUrl ? (
                                                        <img src={participant.imageUrl} alt={participant.firstName} />
                                                    ) : (
                                                        <span>{participant.initials}</span>
                                                    )}
                                                </div>
                                                <div className="participant-info">
                                                    <div className="participant-name">
                                                        {participant.firstName} {participant.lastName}
                                                        {participant.isOriginalMeetingOrganizer && (
                                                            <span className="organizer-label"> (Organizer)</span>
                                                        )}
                                                    </div>
                                                    <div className="participant-title">{participant.jobTitle}</div>
                                                    <div className="participant-company">
                                                        <Building2 size={10} />
                                                        {getParticipantCompanyName(participant)}
                                                    </div>
                                                </div>
                                                {!participant.isAttending && (
                                                    <span className="not-attending-badge">Not Attending</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Cancel Button (for pending meetings where user is organizer) */}
                            {canCancelPendingInvite() && !showCancelPanel && (
                                <div className="drawer-actions-top">
                                    {cancelError && (
                                        <div className="cancel-error">
                                            {cancelError}
                                        </div>
                                    )}
                                    <button
                                        className="cancel-meeting-btn"
                                        onClick={handleCancelInviteClick}
                                        disabled={cancelling}
                                    >
                                        Cancel Invite
                                    </button>
                                </div>
                            )}

                            {/* Accept/Decline Buttons (for pending meetings where user is receiver) */}
                            {canAcceptDecline() && !showDeclinePanel && !showAcceptPanel && (
                                <div className="drawer-actions-top accept-decline-actions">
                                    {actionError && (
                                        <div className="cancel-error">
                                            {actionError}
                                        </div>
                                    )}
                                    <div className="accept-decline-buttons">
                                        <button
                                            className="accept-meeting-btn"
                                            onClick={handleAcceptClick}
                                            disabled={accepting || declining}
                                        >
                                            <CheckCircle size={14} />
                                            Accept
                                        </button>
                                        <button
                                            className="decline-meeting-btn"
                                            onClick={handleDeclineClick}
                                            disabled={accepting || declining}
                                        >
                                            <XCircle size={14} />
                                            Decline
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Cancel/Reschedule Buttons (for any confirmed meeting - both parties can cancel/reschedule) */}
                            {canCancelConfirmed() && !showCancelPanel && !showReschedulePanel && (
                                <div className="drawer-actions-top">
                                    {cancelError && (
                                        <div className="cancel-error">
                                            {cancelError}
                                        </div>
                                    )}
                                    {isPostOptimization() ? (
                                        // Post-optimization: Show both Reschedule and Cancel buttons
                                        <div className="confirmed-meeting-actions">
                                            <button
                                                className="reschedule-meeting-btn"
                                                onClick={handleRescheduleClick}
                                                disabled={cancelling || rescheduling}
                                            >
                                                <Calendar size={14} />
                                                Reschedule
                                            </button>
                                            <button
                                                className="cancel-meeting-btn"
                                                onClick={handleCancelInviteClick}
                                                disabled={cancelling || rescheduling}
                                            >
                                                Cancel Meeting
                                            </button>
                                        </div>
                                    ) : (
                                        // Pre-optimization: Only show Cancel button
                                        <button
                                            className="cancel-meeting-btn"
                                            onClick={handleCancelInviteClick}
                                            disabled={cancelling}
                                        >
                                            Cancel Meeting
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Messages */}
                            {meetingData?.messages && meetingData.messages.length > 0 && (
                                <div className="drawer-section">
                                    <div className="drawer-section-header">
                                        <span>Messages</span>
                                    </div>
                                    <div className="drawer-section-content">
                                        <div className="messages-list">
                                            {meetingData.messages.map((message) => {
                                                const sender = meetingData.participants?.find(p => p.contactId === message.contactId);
                                                return (
                                                    <div key={message.meetingMessageId} className="message-card">
                                                        <div className="message-header">
                                                            <div className="message-sender">
                                                                {sender ? `${sender.firstName} ${sender.lastName}` : 'Unknown'}
                                                            </div>
                                                            <div className="message-date">
                                                                {formatTimeAgo(message.dateCreatedUtc)}
                                                            </div>
                                                        </div>
                                                        <div className="message-content">
                                                            {message.message}
                                                        </div>
                                                        {message.documents && message.documents.length > 0 && (
                                                            <div className="message-attachments">
                                                                {message.documents.map((doc, idx) => (
                                                                    <button
                                                                        key={idx}
                                                                        onClick={(e) => handleAttachmentClick(e, doc.documentUrl)}
                                                                        className="attachment-link"
                                                                    >
                                                                        <Paperclip size={14} />
                                                                        {doc.documentName || 'Attachment'}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Cancel Meeting Panel */}
                            {showCancelPanel && (
                                <div className="cancel-panel" ref={cancelPanelRef}>
                                    <div className="cancel-panel-header">
                                        <h3>Cancel Meeting</h3>
                                    </div>
                                    <div className="cancel-panel-content">
                                        <label className="cancel-note-label">
                                            Add an optional note
                                        </label>
                                        <textarea
                                            className="cancel-note-textarea"
                                            placeholder="Let them know why you're cancelling..."
                                            value={cancelMessage}
                                            onChange={(e) => setCancelMessage(e.target.value.slice(0, 1000))}
                                            maxLength={1000}
                                        />
                                        <div className="cancel-note-count">
                                            {cancelMessage.length}/1000
                                        </div>
                                        {cancelError && (
                                            <div className="cancel-error">
                                                {cancelError}
                                            </div>
                                        )}
                                    </div>
                                    <div className="cancel-panel-actions">
                                        <button
                                            className="cancel-panel-btn cancel-btn-secondary"
                                            onClick={handleCancelPanelClose}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="cancel-panel-btn cancel-btn-primary"
                                            onClick={handleSendCancelClick}
                                            disabled={cancelling}
                                        >
                                            Send
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Decline Meeting Panel */}
                            {showDeclinePanel && (
                                <div className="cancel-panel" ref={declinePanelRef}>
                                    <div className="cancel-panel-header">
                                        <h3>Decline Meeting</h3>
                                    </div>
                                    <div className="cancel-panel-content">
                                        <label className="cancel-note-label">
                                            Add an optional note
                                        </label>
                                        <textarea
                                            className="cancel-note-textarea"
                                            placeholder="Let them know why you're declining..."
                                            value={declineMessage}
                                            onChange={(e) => setDeclineMessage(e.target.value.slice(0, 1000))}
                                            maxLength={1000}
                                        />
                                        <div className="cancel-note-count">
                                            {declineMessage.length}/1000
                                        </div>
                                        {actionError && (
                                            <div className="cancel-error">
                                                {actionError}
                                            </div>
                                        )}
                                    </div>
                                    <div className="cancel-panel-actions">
                                        <button
                                            className="cancel-panel-btn cancel-btn-secondary"
                                            onClick={handleDeclinePanelClose}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="cancel-panel-btn cancel-btn-primary"
                                            onClick={handleSendDecline}
                                            disabled={declining}
                                        >
                                            {declining ? 'Sending...' : 'Send'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Accept Meeting Panel */}
                            {showAcceptPanel && (
                                <div className="cancel-panel" ref={acceptPanelRef}>
                                    <div className="cancel-panel-header">
                                        <h3>Accept Meeting</h3>
                                    </div>
                                    <div className="cancel-panel-content">
                                        {/* Pre-optimization: Show validation status */}
                                        {!isPostOptimization() && (
                                            <>
                                                {loadingAvailability && (
                                                    <div className="availability-loading">
                                                        <LoadingSpinner message="Validating availability..." />
                                                    </div>
                                                )}
                                                {availabilityError && (
                                                    <div className="availability-error">
                                                        <AlertCircle size={20} />
                                                        <span>{availabilityError}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Post-optimization: Show time slot selection */}
                                        {isPostOptimization() && (
                                            <div className="availability-section">
                                                <label className="cancel-note-label">
                                                    Select a meeting time
                                                </label>
                                                {loadingAvailability ? (
                                                    <div className="availability-loading">
                                                        <LoadingSpinner message="Loading available times..." />
                                                    </div>
                                                ) : availabilityError && availabilitySlots.length === 0 ? (
                                                    <div className="availability-error">
                                                        <AlertCircle size={20} />
                                                        <span>{availabilityError}</span>
                                                    </div>
                                                ) : (
                                                    <div className="availability-slots">
                                                        {availabilitySlots.map((slot) => (
                                                            <button
                                                                key={slot.meetingTimeId}
                                                                className={`availability-slot ${selectedTimeSlot === slot.meetingTimeId ? 'selected' : ''}`}
                                                                onClick={() => setSelectedTimeSlot(slot.meetingTimeId)}
                                                            >
                                                                <Calendar size={16} />
                                                                <span>{formatAvailabilitySlot(slot.meetingDateTimeUtc)}</span>
                                                                {selectedTimeSlot === slot.meetingTimeId && (
                                                                    <CheckCircle size={16} className="slot-check" />
                                                                )}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Only show message input if validation passed or is post-optimization */}
                                        {(isPostOptimization() || (!loadingAvailability && !availabilityError)) && (
                                            <>
                                                <label className="cancel-note-label">
                                                    Add an optional note
                                                </label>
                                                <textarea
                                                    className="cancel-note-textarea"
                                                    placeholder="Send a message with your acceptance..."
                                                    value={acceptMessage}
                                                    onChange={(e) => setAcceptMessage(e.target.value.slice(0, 1000))}
                                                    maxLength={1000}
                                                />
                                                <div className="cancel-note-count">
                                                    {acceptMessage.length}/1000
                                                </div>
                                            </>
                                        )}
                                        {actionError && (
                                            <div className="cancel-error">
                                                {actionError}
                                            </div>
                                        )}
                                    </div>
                                    <div className="cancel-panel-actions">
                                        <button
                                            className="cancel-panel-btn cancel-btn-secondary"
                                            onClick={handleAcceptPanelClose}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="cancel-panel-btn cancel-btn-primary"
                                            onClick={handleSendAccept}
                                            disabled={accepting || loadingAvailability || availabilityError || (isPostOptimization() && (!selectedTimeSlot || availabilitySlots.length === 0))}
                                        >
                                            {accepting ? 'Sending...' : 'Accept'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Reschedule Meeting Panel */}
                            {showReschedulePanel && (
                                <div className="cancel-panel" ref={reschedulePanelRef}>
                                    <div className="cancel-panel-header">
                                        <h3>Reschedule Meeting</h3>
                                    </div>
                                    <div className="cancel-panel-content">
                                        <div className="availability-section">
                                            <label className="cancel-note-label">
                                                Select a new meeting time
                                            </label>
                                            {loadingRescheduleAvailability ? (
                                                <div className="availability-loading">
                                                    <LoadingSpinner message="Loading available times..." />
                                                </div>
                                            ) : rescheduleError && rescheduleSlots.length === 0 ? (
                                                <div className="availability-error">
                                                    <AlertCircle size={20} />
                                                    <span>{rescheduleError}</span>
                                                </div>
                                            ) : (
                                                <div className="availability-slots">
                                                    {rescheduleSlots.map((slot) => (
                                                        <button
                                                            key={slot.meetingTimeId}
                                                            className={`availability-slot ${selectedRescheduleSlot === slot.meetingTimeId ? 'selected' : ''}`}
                                                            onClick={() => setSelectedRescheduleSlot(slot.meetingTimeId)}
                                                        >
                                                            <Calendar size={16} />
                                                            <span>{formatAvailabilitySlot(slot.meetingDateTimeUtc)}</span>
                                                            {selectedRescheduleSlot === slot.meetingTimeId && (
                                                                <CheckCircle size={16} className="slot-check" />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <label className="cancel-note-label">
                                            Add an optional note
                                        </label>
                                        <textarea
                                            className="cancel-note-textarea"
                                            placeholder="Let them know why you're rescheduling..."
                                            value={rescheduleMessage}
                                            onChange={(e) => setRescheduleMessage(e.target.value.slice(0, 1000))}
                                            maxLength={1000}
                                        />
                                        <div className="cancel-note-count">
                                            {rescheduleMessage.length}/1000
                                        </div>
                                        {rescheduleError && rescheduleSlots.length > 0 && (
                                            <div className="cancel-error">
                                                {rescheduleError}
                                            </div>
                                        )}
                                    </div>
                                    <div className="cancel-panel-actions">
                                        <button
                                            className="cancel-panel-btn cancel-btn-secondary"
                                            onClick={handleReschedulePanelClose}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="cancel-panel-btn cancel-btn-primary"
                                            onClick={handleSendReschedule}
                                            disabled={rescheduling || loadingRescheduleAvailability || !selectedRescheduleSlot || rescheduleSlots.length === 0}
                                        >
                                            {rescheduling ? 'Sending...' : 'Reschedule'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <>
                    <div className="confirm-dialog-overlay" onClick={handleConfirmDialogClose} />
                    <div className="confirm-dialog">
                        <div className="confirm-dialog-content">
                            <h3>You're about to cancel this request.</h3>
                            <p>
                                Are you sure you want to cancel this meeting request with {getOtherCompanyName()}? This action can't be undone.
                            </p>
                        </div>
                        <div className="confirm-dialog-actions">
                            <button
                                className="confirm-dialog-btn btn-secondary"
                                onClick={handleConfirmDialogClose}
                                disabled={cancelling}
                            >
                                Go Back
                            </button>
                            <button
                                className="confirm-dialog-btn btn-danger"
                                onClick={handleConfirmCancel}
                                disabled={cancelling}
                            >
                                {cancelling ? 'Cancelling...' : 'Cancel Invite'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Participant Profile Drawer */}
            {profileDrawerOpen && (
                <>
                    <div className="attendee-drawer-overlay participant-profile-overlay" onClick={closeProfileDrawer} />
                    <div className={`attendee-profile-drawer participant-profile-drawer ${profileDrawerOpen ? 'open' : ''}`}>
                        <div className="attendee-drawer-header">
                            <span className="attendee-drawer-entity-tag">
                                PROFILE
                            </span>
                            <button className="attendee-drawer-close" onClick={closeProfileDrawer} title="Close">
                                <X size={20} />
                            </button>
                        </div>

                        {profileLoading ? (
                            <div className="attendee-drawer-loading">
                                <LoadingSpinner message="Loading profile..." />
                            </div>
                        ) : (
                            <div className="attendee-drawer-content">
                                {/* Profile Header */}
                                <div className="attendee-drawer-profile-header">
                                    <div className="attendee-drawer-banner"></div>
                                    <div className="attendee-drawer-avatar-section">
                                        <div className="attendee-drawer-avatar">
                                            {(participantProfileData?.image_url || selectedParticipant?.imageUrl) ? (
                                                <img
                                                    src={participantProfileData?.image_url || selectedParticipant?.imageUrl}
                                                    alt={`${selectedParticipant?.firstName} ${selectedParticipant?.lastName}`}
                                                />
                                            ) : (
                                                <span>{getProfileInitials()}</span>
                                            )}
                                        </div>
                                        <h2 className="attendee-drawer-name">
                                            {participantProfileData?.first_name || selectedParticipant?.firstName} {participantProfileData?.last_name || selectedParticipant?.lastName}
                                        </h2>
                                        {(participantProfileData?.job_title || selectedParticipant?.jobTitle) && (
                                            <p className="attendee-drawer-title">
                                                {participantProfileData?.job_title || selectedParticipant?.jobTitle}
                                            </p>
                                        )}
                                        {(participantProfileData?.company_name || selectedParticipant?.companyName) && (
                                            <p className="attendee-drawer-company">
                                                {participantProfileData?.company_name || selectedParticipant?.companyName}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* About Section */}
                                {participantProfileData && (
                                    <div className="attendee-drawer-section">
                                        <h3 className="attendee-drawer-section-title">About</h3>

                                        {participantProfileData.short_bio && (
                                            <p className="attendee-drawer-bio">{participantProfileData.short_bio}</p>
                                        )}

                                        <div className="attendee-drawer-info-grid">
                                            {participantProfileData.job_title && (
                                                <div className="attendee-drawer-info-item">
                                                    <span className="info-label">Role</span>
                                                    <span className="info-value">{participantProfileData.job_title}</span>
                                                </div>
                                            )}
                                            {participantProfileData.company_name && (
                                                <div className="attendee-drawer-info-item">
                                                    <span className="info-label">Company</span>
                                                    <span className="info-value">{participantProfileData.company_name}</span>
                                                </div>
                                            )}
                                            {(participantProfileData.city || participantProfileData.state_province || participantProfileData.country) && (
                                                <div className="attendee-drawer-info-item">
                                                    <span className="info-label">Location</span>
                                                    <span className="info-value">
                                                        {[participantProfileData.city, participantProfileData.state_province, participantProfileData.country]
                                                            .filter(Boolean)
                                                            .join(', ')}
                                                    </span>
                                                </div>
                                            )}
                                            {participantProfileData.timezone && (
                                                <div className="attendee-drawer-info-item">
                                                    <span className="info-label">Time Zone</span>
                                                    <span className="info-value">{participantProfileData.timezone}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Companies Section */}
                                {participantProfileData?.companies && participantProfileData.companies.length > 0 && (
                                    <div className="attendee-drawer-section">
                                        <h3 className="attendee-drawer-section-title">Companies</h3>
                                        <div className="attendee-drawer-companies">
                                            {participantProfileData.companies.map((company) => (
                                                <div key={company.CompanyId} className="attendee-drawer-company-card">
                                                    {company.LogoUrl && (
                                                        <img
                                                            src={company.LogoUrl}
                                                            alt={company.Name}
                                                            className="company-logo"
                                                        />
                                                    )}
                                                    <div className="company-details">
                                                        <h4>{company.Name}</h4>
                                                        {(company.Location || company.City || company.StateProvince || company.Country) && (
                                                            <p>
                                                                {company.Location ||
                                                                    [company.City, company.StateProvince, company.Country]
                                                                        .filter(Boolean)
                                                                        .join(', ')}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Social Links Section */}
                                {participantProfileData && (participantProfileData.linkedin_url || participantProfileData.instagram) && (
                                    <div className="attendee-drawer-section">
                                        <h3 className="attendee-drawer-section-title">Social Links</h3>
                                        <div className="attendee-drawer-social">
                                            {participantProfileData.linkedin_url && (
                                                <a
                                                    href={participantProfileData.linkedin_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="social-link linkedin"
                                                >
                                                    <Linkedin size={18} />
                                                    LinkedIn
                                                </a>
                                            )}
                                            {participantProfileData.instagram && (
                                                <a
                                                    href={participantProfileData.instagram.startsWith('http') ? participantProfileData.instagram : `https://instagram.com/${participantProfileData.instagram.replace(/^@/, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="social-link instagram"
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                                                        <path d="M10 0C7.28 0 6.94 0.01 5.88 0.06C2.73 0.22 0.22 2.73 0.06 5.88C0.01 6.94 0 7.28 0 10C0 12.72 0.01 13.06 0.06 14.12C0.22 17.27 2.73 19.78 5.88 19.94C6.94 19.99 7.28 20 10 20C12.72 20 13.06 19.99 14.12 19.94C17.27 19.78 19.78 17.27 19.94 14.12C19.99 13.06 20 12.72 20 10C20 7.28 19.99 6.94 19.94 5.88C19.78 2.73 17.27 0.22 14.12 0.06C13.06 0.01 12.72 0 10 0ZM10 1.8C12.67 1.8 12.99 1.81 14.04 1.86C16.78 1.99 18.01 3.24 18.14 5.96C18.19 7.01 18.2 7.33 18.2 10C18.2 12.67 18.19 12.99 18.14 14.04C18.01 16.76 16.78 18.01 14.04 18.14C12.99 18.19 12.67 18.2 10 18.2C7.33 18.2 7.01 18.19 5.96 18.14C3.22 18.01 1.99 16.76 1.86 14.04C1.81 12.99 1.8 12.67 1.8 10C1.8 7.33 1.81 7.01 1.86 5.96C1.99 3.24 3.22 1.99 5.96 1.86C7.01 1.81 7.33 1.8 10 1.8ZM10 4.86C7.16 4.86 4.86 7.16 4.86 10C4.86 12.84 7.16 15.14 10 15.14C12.84 15.14 15.14 12.84 15.14 10C15.14 7.16 12.84 4.86 10 4.86ZM10 13.33C8.16 13.33 6.67 11.84 6.67 10C6.67 8.16 8.16 6.67 10 6.67C11.84 6.67 13.33 8.16 13.33 10C13.33 11.84 11.84 13.33 10 13.33ZM15.34 3.46C14.67 3.46 14.13 4 14.13 4.67C14.13 5.34 14.67 5.88 15.34 5.88C16.01 5.88 16.54 5.34 16.54 4.67C16.54 4 16.01 3.46 15.34 3.46Z" />
                                                    </svg>
                                                    Instagram
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>
                </>
            )}

            {/* Entity Profile Drawer (for nonprofit/philanthropist companies) */}
            {entityProfileDrawerOpen && (
                <>
                    <div className="drawer-overlay entity-profile-overlay" onClick={closeEntityProfileDrawer} />
                    <div className={`entity-profile-drawer nonprofit-drawer ${entityProfileDrawerOpen ? 'open' : ''}`}>
                        <div className="drawer-header">
                            <h2>{entityProfileData?.CompanyName || counterpartyCompanyName || 'Loading...'}</h2>
                            <button className="drawer-close-btn" onClick={closeEntityProfileDrawer} aria-label="Close drawer">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="drawer-content">
                            {entityProfileLoading ? (
                                <div className="drawer-loading">
                                    <LoadingSpinner message="Loading profile..." />
                                </div>
                            ) : !entityProfileData ? (
                                <div className="drawer-empty">
                                    <p>Could not load profile.</p>
                                </div>
                            ) : (
                                <>
                                    {/* Entity Tag */}
                                    {counterpartyEntityType && (
                                        <span className="drawer-entity-tag">
                                            {counterpartyEntityType}
                                        </span>
                                    )}

                                    {/* Organization Contacts */}
                                    {entityProfileData.contacts && entityProfileData.contacts.length > 0 && (
                                        <div className="drawer-contacts-section">
                                            {entityProfileData.contacts.map((contact) => (
                                                <div
                                                    key={contact.ContactId}
                                                    className="drawer-contact-item"
                                                    onClick={() => window.open(`/user/${contact.ContactId}`, '_blank')}
                                                    title="View profile"
                                                >
                                                    {contact.ImageUrl ? (
                                                        <img
                                                            src={contact.ImageUrl}
                                                            alt={`${contact.FirstName} ${contact.LastName}`}
                                                            className="drawer-contact-avatar"
                                                        />
                                                    ) : (
                                                        <div className="drawer-contact-avatar-placeholder">
                                                            {contact.FirstName?.[0]}{contact.LastName?.[0]}
                                                        </div>
                                                    )}
                                                    <div className="drawer-contact-info">
                                                        <div className="drawer-contact-name">
                                                            {contact.FirstName} {contact.LastName}
                                                        </div>
                                                        {contact.JobTitle && (
                                                            <div className="drawer-contact-title">
                                                                {contact.JobTitle}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Profile Fields */}
                                    {entityQuestions
                                        ?.filter((question) => {
                                            const value = entityProfileData?.answers?.[question.question_id];
                                            if (value === null || value === undefined || value === '') {
                                                return false;
                                            }
                                            // Hide specific financial questions for philanthropists
                                            const questionText = (question.short_description || question.question_text || '').toLowerCase();
                                            if (questionText.includes('average annual philanthropic giving') && questionText.includes('last three years')) {
                                                return false;
                                            }
                                            if (questionText.includes('philanthropic giving projected to grow')) {
                                                return false;
                                            }
                                            if (questionText.includes('projected capacity to give to new charitable organizations')) {
                                                return false;
                                            }
                                            return true;
                                        })
                                        .sort((a, b) => {
                                            // Custom sort order
                                            const getOrder = (q) => {
                                                const text = (q.short_description || q.question_text || '').toLowerCase();
                                                if (text.includes('philanthropy type') || text.includes('type of philanthropy') || text.includes('donor type')) return 1;
                                                if (text.includes('organization description') || text.includes('org description') || text.includes('mission')) return 2;
                                                if (text.includes('areas of interest') || text.includes('focus area') || text.includes('areas of philanthropic giving')) return 3;
                                                if (text.includes('geographic') && (text.includes('footprint') || text.includes('focus'))) return 4;
                                                if (text.includes('potential geographies')) return 5;
                                                if (text.includes('current approach')) return 6;
                                                return 100;
                                            };
                                            return getOrder(a) - getOrder(b);
                                        })
                                        .map((question) => {
                                            const value = entityProfileData?.answers?.[question.question_id];
                                            let labelText = question.short_description || question.question_text;

                                            // Transform labels for better display
                                            if (labelText?.toLowerCase().includes('donor areas of interest')) {
                                                labelText = labelText.replace(/donor\s+/i, '');
                                            }
                                            if (labelText?.toLowerCase().includes('geographic footprint')) {
                                                labelText = labelText.replace(/footprint/i, 'Focus');
                                            }
                                            if (labelText?.toLowerCase().includes('primary areas of philanthropic giving')) {
                                                labelText = 'Areas of Philanthropic Giving';
                                            }
                                            if (labelText?.toLowerCase().includes('geographic focus') && labelText?.toLowerCase().includes('select as many')) {
                                                labelText = 'Geographic Focus';
                                            }
                                            if (labelText?.toLowerCase().includes('potential geographies for future giving')) {
                                                labelText = 'Potential Geographies For Future Giving';
                                            }
                                            if (labelText?.toLowerCase().includes('current approach to philanthropic giving')) {
                                                labelText = 'Current Approach To Philanthropic Giving';
                                            }
                                            if (labelText?.toLowerCase().startsWith('financials')) {
                                                labelText = 'Financials';
                                            }
                                            if (labelText) {
                                                labelText = labelText.replace(/\s*\(check all that apply\)/gi, '');
                                                labelText = labelText.replace(/\s*\(select as many as apply\)/gi, '');
                                            }

                                            return (
                                                <div key={question.question_id} className="drawer-field">
                                                    <label className="drawer-field-label">
                                                        {labelText}
                                                    </label>
                                                    <div className="drawer-field-value">
                                                        {renderEntityDrawerValue(value, question)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </>
                            )}
                        </div>

                        <div className="drawer-footer">
                            <div className="meeting-exists-message">
                                <CheckCircle size={18} />
                                <span>
                                    {meetingData?.meetingStatusId === 2 
                                        ? 'You have a confirmed meeting with this organization'
                                        : meetingData?.meetingStatusId === 1
                                            ? 'You have a pending meeting request with this organization'
                                            : 'You have a meeting interaction with this organization'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default MeetingDetailsDrawer;
